// Persistencia local del jugador (récord, ajustes).
import { avatarToLegacyProfile, createDefaultAvatar, normalizeAvatar } from '../avatar/AvatarConfig';
// Esta capa está aislada: en el futuro puede sincronizar con la nube vía ApiClient
// sin cambiar el resto del juego.

const KEY = 'tmag_player_v2';

const DEFAULT = {
  best: null,
  settings: { muted: false },
  runs: 0,
  lastRun: null,
  // Un perfil por navegador/dispositivo. No se guarda correo, escuela ni nombre legal.
  profile: { nickname: '', explorer: 'explorador', color: 'cyan', avatar: createDefaultAvatar(), onboarded: false },
  // Guía inicial de juego: se muestra una vez por navegador/perfil y puede
  // repetirse al publicar una mejora importante de esa guía.
  tutorial: { seen: false, version: 0 },
};

export const PlayerData = {
  load() {
    try {
      return { ...DEFAULT, ...(JSON.parse(localStorage.getItem(KEY)) || {}) };
    } catch {
      return { ...DEFAULT };
    }
  },

  save(data) {
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch {
      /* almacenamiento no disponible: ignorar */
    }
  },


  // Perfil local y seudónimo: pensado para uso escolar sin crear cuentas.
  getProfile() {
    const profile = this.load().profile || {};
    const explorer = profile.explorer === 'exploradora' ? 'exploradora' : 'explorador';
    const avatar = normalizeAvatar(profile.avatar, { explorer, color: profile.color });
    // Mantiene explorer/color como contrato de compatibilidad con gameplay existente.
    return {
      nickname: '',
      explorer: avatar.base,
      color: avatar.suitAccent,
      avatar,
      onboarded: false,
      ...profile,
      avatar,
      explorer: avatar.base,
      color: avatar.suitAccent,
    };
  },

  hasProfile() {
    return !!this.getProfile().onboarded;
  },

  hasSeenTutorial() {
    // La versión permite enseñar una guía mejorada una vez a quienes vieron
    // la tarjeta anterior, sin repetirla después.
    const tutorial = this.load().tutorial || {};
    return !!tutorial.seen && (tutorial.version || 0) >= 5;
  },

  markTutorialSeen() {
    const data = this.load();
    data.tutorial = { ...(data.tutorial || {}), seen: true, version: 5, seenAt: Date.now() };
    this.save(data);
  },

  saveProfile(patch) {
    const data = this.load();
    const current = this.getProfile();
    const nickname = String(patch?.nickname ?? current.nickname ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 18);
    const avatarPatch = patch?.avatar || {};
    const avatar = normalizeAvatar({
      ...current.avatar,
      ...avatarPatch,
      base: avatarPatch.base ?? patch?.explorer ?? current.avatar.base,
      suitAccent: avatarPatch.suitAccent ?? patch?.color ?? current.avatar.suitAccent,
    }, {
      explorer: patch?.explorer ?? current.explorer,
      color: patch?.color ?? current.color,
    });

    data.profile = {
      ...current,
      ...patch,
      ...avatarToLegacyProfile(avatar),
      avatar,
      nickname: nickname || 'Explorador/a',
      onboarded: true,
      updatedAt: Date.now(),
    };
    this.save(data);
    return data.profile;
  },

  getBest() {
    return this.load().best || { score: 0, streak: 0, solved: 0 };
  },

  submitRun(run) {
    const data = this.load();
    const prevBest = data.best?.score || 0;
    if (run.score > prevBest) {
      data.best = {
        score: run.score,
        streak: run.bestStreak,
        solved: run.solved,
        date: Date.now(),
      };
    }
    data.lastRun = run;
    data.runs = (data.runs || 0) + 1;
    this.save(data);
    return data.best;
  },

  getSettings() {
    return this.load().settings || { muted: false };
  },

  setSettings(patch) {
    const data = this.load();
    data.settings = { ...data.settings, ...patch };
    this.save(data);
  },

  // Progreso de planetas por galaxia (modular; base para nube/rankings futuros).
  markPlanetComplete(id) {
    const data = this.load();
    data.planets = { ...(data.planets || {}), [id]: true };
    this.save(data);
  },

  isPlanetComplete(id) {
    return !!(this.load().planets || {})[id];
  },

  // Mejor puntuación INDEPENDIENTE por planeta (modular; cada planeta guarda la suya).
  getPlanetBest(id) {
    return (this.load().planetBest || {})[id] || 0;
  },

  submitPlanetScore(id, score) {
    const data = this.load();
    const pb = data.planetBest || {};
    const prev = pb[id] || 0;
    const isRecord = score > prev;
    if (isRecord) {
      pb[id] = score;
      data.planetBest = pb;
      this.save(data);
    }
    return { best: Math.max(prev, score), isRecord };
  },

  // Progreso por MISIÓN dentro de un planeta.
  // Las misiones se desbloquean en secuencia: cada misión completada abre la siguiente.
  getMissionData(planetId) {
    return (this.load().missionProgress || {})[planetId] || { completed: {}, best: {}, attempts: {} };
  },

  isMissionComplete(planetId, idx) {
    return !!this.getMissionData(planetId).completed[idx];
  },

  // Registro formativo: muestra que el estudiante practicó aunque aún no
  // haya dominado la misión. No desbloquea contenido por sí solo.
  getMissionAttempt(planetId, idx) {
    return this.getMissionData(planetId).attempts?.[idx] || null;
  },

  recordMissionAttempt(planetId, idx, run) {
    const data = this.load();
    const mp = data.missionProgress || {};
    const pm = mp[planetId] || { completed: {}, best: {}, attempts: {} };
    const previous = pm.attempts?.[idx] || {};
    pm.attempts = {
      ...(pm.attempts || {}),
      [idx]: {
        count: (previous.count || 0) + 1,
        correct: run.correct,
        wrong: run.wrong,
        accuracy: run.accuracy,
        finished: !!run.won,
        date: Date.now(),
      },
    };
    mp[planetId] = pm;
    data.missionProgress = mp;
    this.save(data);
    return pm.attempts[idx];
  },

  // Historial por microdestreza. Se guarda localmente y luego podrá
  // sincronizarse con cuentas de estudiantes sin cambiar la lógica del juego.
  recordSkillAttempt(planetId, missionIndex, skillTag, correct) {
    if (!skillTag) return null;
    const data = this.load();
    const all = data.skillProgress || {};
    const key = planetId + ':' + missionIndex + ':' + skillTag;
    const previous = all[key] || { attempts: 0, correct: 0, wrong: 0 };
    const next = {
      attempts: previous.attempts + 1,
      correct: previous.correct + (correct ? 1 : 0),
      wrong: previous.wrong + (correct ? 0 : 1),
      date: Date.now(),
    };
    all[key] = next;
    data.skillProgress = all;
    this.save(data);
    return next;
  },

  // Devuelve hasta tres microdestrezas con mayor necesidad de refuerzo
  // dentro de una misión. La práctica adaptativa nunca cambia el banco oficial.
  getPracticeFocus(planetId, missionIndex, limit = 3) {
    const prefix = planetId + ':' + missionIndex + ':';
    return Object.entries(this.load().skillProgress || {})
      .filter(([key, stat]) => key.startsWith(prefix) && stat.wrong > 0)
      .sort(([, a], [, b]) => {
        const rateA = a.wrong / Math.max(1, a.attempts);
        const rateB = b.wrong / Math.max(1, b.attempts);
        return rateB - rateA || b.wrong - a.wrong;
      })
      .slice(0, limit)
      .map(([key]) => key.slice(prefix.length));
  },

  isMissionUnlocked(planetId, idx) {
    return idx === 0 || this.isMissionComplete(planetId, idx - 1);
  },

  getMissionBest(planetId, idx) {
    const mission = this.getMissionData(planetId);
    // Una partida incompleta nunca cuenta como récord de la misión.
    return mission.completed[idx] ? (mission.best[idx] || 0) : 0;
  },

  markMissionComplete(planetId, idx) {
    const data = this.load();
    const mp = data.missionProgress || {};
    const pm = mp[planetId] || { completed: {}, best: {} };
    pm.completed = { ...pm.completed, [idx]: true };
    mp[planetId] = pm;
    data.missionProgress = mp;
    this.save(data);
  },

  submitMissionScore(planetId, idx, score) {
    const data = this.load();
    const mp = data.missionProgress || {};
    const pm = mp[planetId] || { completed: {}, best: {} };
    const prev = pm.best[idx] || 0;
    const isRecord = score > prev;
    if (isRecord) {
      pm.best = { ...pm.best, [idx]: score };
      mp[planetId] = pm;
      data.missionProgress = mp;
      this.save(data);
    }
    return { best: Math.max(prev, score), isRecord };
  },

  countMissionsComplete(planetId, total) {
    const c = this.getMissionData(planetId).completed;
    let n = 0;
    for (let i = 0; i < total; i++) if (c[i]) n++;
    return n;
  },
};
